import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/Card/Card'; // Assuming your Card component is here
import TypingGame from '../Game/TypingGame'; // Import the TypingGame component
import { Code, Paintbrush, Gamepad } from 'lucide-react'; // Added Lucide icons for consistency

const KidsHub = () => {
    // State to control whether the TypingGame is shown
    const [showTypingGame, setShowTypingGame] = useState(false);

    // Intersection Observer logic for fade-in animations
    const observedSections = useRef(new Set());
    const setSectionRef = useCallback((node) => {
        if (node) {
            observedSections.current.add(node);
        }
    }, []);

    const handleSectionIntersect = useCallback((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up-active');
                // Remove initial opacity and transform classes if they were set for the animation
                entry.target.classList.remove('opacity-0', 'translate-y-10');
                if (entry.target.currentObserver) {
                    entry.target.currentObserver.unobserve(entry.target);
                }
            }
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleSectionIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1 // Trigger when 10% of the element is visible
        });

        observedSections.current.forEach(section => {
            if (section) {
                observer.observe(section);
                section.currentObserver = observer; // Store observer reference on the element
            }
        });

        return () => {
            observedSections.current.forEach(section => {
                if (section && section.currentObserver) {
                    section.currentObserver.unobserve(section);
                }
            });
            observer.disconnect();
            observedSections.current.clear();
        };
    }, [handleSectionIntersect]);

    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Global Styles for animations (same as App.js for consistency) */}
            <style>
                {`
                .fade-in-up { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease-out, transform 0.8s ease-out; }
                .fade-in-up-active { opacity: 1; transform: translateY(0); }
                `}
            </style>

            {/* Conditionally render the game or the main Kids Hub content */}
            {showTypingGame ? (
                // Render the TypingGame when showTypingGame is true
                <Card title="Cyber Safari: Typing Challenge" fullContent={true}>
                    <TypingGame />
                    {/* Add a button to close the game and go back to Kids Hub content */}
                    <button
                        onClick={() => setShowTypingGame(false)}
                        className="mt-4 px-6 py-2 bg-[#C9B072] text-[#0A1128] font-semibold text-md rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90"
                        style={{ position: 'absolute', bottom: '1rem', left: '50%', transform: 'translateX(-50%)', zIndex: 40 }}
                    >
                        Close Game
                    </button>
                </Card>
            ) : (
                // Render the main Kids Hub content when showTypingGame is false
                <>
                    {/* Section 1: Hero/Introduction - Blue Background */}
                    <section
                        className="py-20 text-center px-6 relative overflow-hidden bg-cover bg-center bg-no-repeat"
                        style={{
                            backgroundImage: `url('https://i.imgur.com/jk9W0tn.jpeg')`,
                        }}
                    >
                        {/* Overlay for background image brightness */}
                        <div className="absolute inset-0 bg-black bg-opacity-20 z-0"></div> {/* Adjusted opacity to 20% */}
                        <div className="container mx-auto relative z-10 flex flex-col items-center justify-center"> {/* z-10 ensures content is above the background */}
                            {/* Added a div for the rounded black overlay */}
                            <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                                <h1 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-6">
                                    JAKOM Kids Hub
                                </h1>
                                <p className="text-xl md:text-2xl text-[#F8F8F8] max-w-3xl mx-auto mb-10">
                                    Ignite curiosity and foster a love for technology and creativity in the next generation! Our Kids Hub offers engaging and interactive programs designed to introduce children to the exciting world of coding, design, and problem-solving through fun activities.
                                </p>
                                <Link to="/get-a-quote" className="px-8 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                                    Inquire About Programs
                                </Link>
                            </div>
                        </div>
                    </section>

                    {/* Section 2: Programs/Offerings - Whitish Background */}
                    <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                        <div className="container mx-auto">
                            <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-12 fade-in-up" ref={setSectionRef}>
                                Our Engaging Programs
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                                {/* Coding Workshops Card */}
                                <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.1s' }}>
                                    <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                        <Code size={48} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Coding Workshops</h3>
                                    <p className="text-gray-700">Fun and interactive coding lessons for all ages, building foundational tech skills.</p>
                                </div>
                                {/* Creative Design Challenges Card */}
                                <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.2s' }}>
                                    <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                        <Paintbrush size={48} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Creative Design Challenges</h3>
                                    <p className="text-gray-700">Spark imagination through digital art, graphic design, and multimedia projects.</p>
                                </div>
                                {/* Fun Games Card - Now clickable to show the game */}
                                <div
                                    className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up cursor-pointer transform transition-transform duration-300 hover:scale-105"
                                    ref={setSectionRef}
                                    style={{ animationDelay: '0.3s' }}
                                    onClick={() => setShowTypingGame(true)} // Set showTypingGame to true on click
                                >
                                    <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                        <Gamepad size={48} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Fun & Educational Games</h3>
                                    <p className="text-gray-700">Enjoy engaging and interactive games that make learning exciting!</p>
                                    <button className="mt-4 px-4 py-2 bg-[#4CAF50] text-[#F8F8F8] font-semibold rounded-full hover:bg-opacity-90">
                                        Play Now!
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: Call to Action - Blue Background */}
                    <section className="py-20 bg-[#0A1128] border-t border-[#C9B072] text-center px-6">
                        <div className="container mx-auto max-w-3xl">
                            <h2 className="text-4xl sm:text-5xl font-extrabold text-[#F8F8F8] mb-8 fade-in-up" ref={setSectionRef}>
                                Ready to Spark Their Potential?
                            </h2>
                            <p className="text-xl text-[#CCD2E3] mb-10 fade-in-up" ref={setSectionRef}>
                                Enroll your child in our Kids Hub programs and watch them grow with technology.
                            </p>
                            <Link to="/contact-us" className="px-12 py-4 bg-[#4CAF50] text-[#F8F8F8] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse inline-flex items-center justify-content-center fade-in-up" ref={setSectionRef}>
                                Enroll Your Child Today!
                            </Link>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default KidsHub;
